import React, { useEffect, useRef } from "react";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FormInput, registerObject } from "go-form";
import { MoneyApi } from "go-core/api/types";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import FormMoneyInput from "go-form/components/FormMoneyInput";
import FormNumberInput from "go-form/components/FormNumberInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { apiOrganization } from "../../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	InvoiceApi,
	InvoiceCalculationType,
	InvoiceItemApi,
	ProductApi,
	TaxApi,
} from "../../../../../../../../../../../services/Api/Organization/types";
import { calculatePriceGross } from "../../../../../utils/summaryTaxesUtils";
import InvoiceFormItemsTableSummary from "./InvoiceFormItemsTableSummary";

interface ProductSearchSelect extends ProductApi {
	label: string;
}

interface Props {
	form: UseFormReturn<InvoiceApi>;
	searchProducts(search: string, params: Record<string, any>, options?: Record<string, any>): Promise<ProductApi[]>;
	taxes: TaxApi[];
}

const InvoiceFormItemsTableBody = ({ form, searchProducts, taxes }: Props) => {
	const organization = useSelector(selectOrganization);
	const {
		watch,
		control,
		formState: { errors },
		register,
		getValues,
		setValue,
	} = form;
	const { fields, remove, append } = useFieldArray({
		control,
		name: "items",
		keyName: "key",
	});
	const currency = organization.currency || "";
	const watchedItems = watch("items");
	const itemsAsString = JSON.stringify(watchedItems);
	const optionsRef = useRef<ProductSearchSelect[]>([]);
	const { t } = useTranslation();

	const getMeasureTypeOptions = ["ITEM", "KILOGRAM", "LITER", "PACKAGE"].map((measureType) => ({
		value: measureType,
		label: t(`enums.products.measure_types.${measureType}`),
	}));

	const wasLastItemUpdated = (items: InvoiceItemApi[]) => {
		const lastItem = items[items.length - 1];

		return !!(
			lastItem?.gtu ||
			lastItem?.name ||
			lastItem?.pkwiu ||
			lastItem?.price?.amount ||
			lastItem?.quantity ||
			lastItem?.tax?.id ||
			lastItem?.volume ||
			lastItem?.measure_type
		);
	};

	useEffect(() => {
		if (wasLastItemUpdated(watchedItems)) {
			append({ name: "" });
		}
	}, [itemsAsString]);

	const handleParseOptionValue = (option: ProductSearchSelect) => {
		optionsRef.current = optionsRef.current.find((o) => o.id === option.id)
			? [...optionsRef.current]
			: [...optionsRef.current, option];
		return option.label;
	};

	const handleChangeProduct = (index: number) => {
		const name = getValues(`items.${index}.name`);
		const option = optionsRef.current.find((o) => o.name === name);

		if (!option) {
			const currentPriceAmount = watch(`items.${index}.price.amount`);
			const currentTax = watch(`items.${index}.tax`);
			setValue(`items.${index}.price.amount`, currentPriceAmount);

			if (currentTax?.id && currentTax) {
				handleChangeTax(currentTax?.id, currentTax, index);
			}

			setValue(`items.${index}.name`, name);
		} else {
			setValue(`items.${index}.price.amount`, option?.last_price_money?.amount);
			setValue(`items.${index}.measure_type`, option.measure_type);
			const tax = taxes.find(({ id }) => id === Number(option?.tax_id));

			if (tax) {
				handleChangeTax(option.tax_id, tax, index);
			}
		}
	};

	const handleRemove = (index: number) => {
		remove(index);
	};

	const searchTax = (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return apiOrganization.getTaxSearchSelect(search, params, {
			cancelToken: options?.token,
		});
	};

	const handleChangeTax = (taxId: number, tax: TaxApi, index: number) => {
		registerObject(register, `items.${index}.tax`, ["amount", "name", "code", "id"]);
		setValue(`items.${index}.tax`, tax);
		setValue(`items.${index}.tax_id`, taxId, { shouldDirty: true });
	};

	const handleCalculateItemTotalPrice = (quantity: number, money: MoneyApi) => {
		if (money && money.amount) {
			const newAmount = quantity * parseFloat(money.amount.toString());

			return newAmount.toFixed(2);
		}

		return `0.00`;
	};

	const getTotalPriceGross = () => {
		const amount = watchedItems
			.map((item) => {
				if (!item.quantity || !item.price || item.tax?.amount === undefined || item.tax?.amount === null)
					return 0;

				return calculatePriceGross(
					item.price?.amount,
					item.quantity,
					item.tax?.amount,
					watch("calculation_type") === InvoiceCalculationType.GROSS
				);
			})
			.reduce((acc, cur) => acc + cur, 0);

		return { amount, currency };
	};

	return (
		<tbody>
			{watchedItems.map((item, index) => (
				<tr key={fields[index].key}>
					<td className="action">
						<FormInput
							type={"hidden"}
							errors={errors}
							register={register}
							name={`items.${index}.id`}
							defaultValue={item.id || undefined}
						/>
					</td>
					<td className="d-flex column-gap-1 align-items-center">
						<span>{`${index + 1}.`}</span>
						<FormSelectGroup
							name={`items.${index}.name`}
							errors={errors}
							forceCreateOption
							getOptionValue={(option) => handleParseOptionValue(option)}
							onChange={() => handleChangeProduct(index)}
							defaultValue={{
								label: item?.name,
							}}
							loadOptions={searchProducts}
							control={control}
							data-testid={`items.${index}.name`}
							onBlurShouldForceChangeOption
							forceInputChange
							className="d-inline-block"
						/>
					</td>
					<td>
						<FormInput
							register={register}
							defaultValue={item.pkwiu || undefined}
							name={`items.${index}.pkwiu`}
							errors={errors}
						/>
					</td>
					<td>
						<FormInput
							register={register}
							defaultValue={item.gtu || undefined}
							name={`items.${index}.gtu`}
							errors={errors}
						/>
					</td>
					<td>
						<FormNumberInput
							control={control}
							defaultValue={item.volume || undefined}
							name={`items.${index}.volume`}
							errors={errors}
							suffix=""
						/>
					</td>
					<td>
						<FormNumberInput
							control={control}
							defaultValue={item.quantity || undefined}
							name={`items.${index}.quantity`}
							errors={errors}
							suffix=""
						/>
					</td>
					<td>
						<FormSelectGroup
							name={`items.${index}.measure_type`}
							errors={errors}
							options={getMeasureTypeOptions}
							control={control}
							data-testid={`items.${index}.measure_type`}
						/>
					</td>
					<td>
						<FormMoneyInput
							errors={errors}
							name={`items.${index}.price.amount`}
							currency={currency}
							control={control}
						/>
					</td>
					<td className="nowrap">
						{handleCalculateItemTotalPrice(item.quantity || 0, item.price || { amount: 0, currency })}
					</td>
					<td>
						<FormSelectGroup
							name={`items.${index}.tax_id`}
							errors={errors}
							onChange={(id, obj) => handleChangeTax(id, obj, index)}
							isClearable={false}
							getOptionValue={(option) => option.id}
							defaultValue={{
								id: item?.tax?.id,
								label: taxes.find(({ id }) => id === item?.tax?.id)?.name,
							}}
							loadOptions={searchTax}
							control={control}
							data-testid={`items.${index}.tax_id`}
						/>
					</td>
					<td className="action">
						{!(fields.length === 1 && index === 0) && (
							<RemoveSVG className="icon" onClick={() => handleRemove(index)} />
						)}
					</td>
				</tr>
			))}
			<InvoiceFormItemsTableSummary
				calculationType={watch("calculation_type")}
				totalPrice={getTotalPriceGross()}
				paidPrice={watch("summary.price_paid")}
			/>
		</tbody>
	);
};

export default InvoiceFormItemsTableBody;
